import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Icon, Label, Popup } from "simplydo/react/ui";

import {
  annualImpactValue,
  generateImpactText,
  getImpactIconName,
  useImpactStageMap,
} from "../Ideas/ImpactStatements/statementUtils";
import {
  type IdeaImpactDisplayMeasure,
  type IdeaImpactsEntry,
  listIdeaImpactMeasures,
} from "./types";

type LaneCardImpactsProps = {
  showUserEstimate: boolean;
  impactData: IdeaImpactsEntry | undefined;
};

export const LaneCardImpacts = ({
  showUserEstimate,
  impactData,
}: LaneCardImpactsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const stageMap = useImpactStageMap();

  const allImpacts = listIdeaImpactMeasures(impactData);
  const pinnedImpacts = allImpacts.filter((i) => i.isPinned);
  const hiddenImpactsCount =
    (allImpacts?.length || 0) - (pinnedImpacts?.length || 0);

  const hasUserEstimate = impactData?.completedImpactStages?.userEstimate;
  const hasProjectEstimate = impactData?.completedImpactStages?.projectEstimate;
  const hasFinalOutcome = impactData?.completedImpactStages?.finalOutcome;

  const currentStage =
    hasFinalOutcome ? "finalOutcome"
    : hasProjectEstimate ? "projectEstimate"
    : hasUserEstimate ? "userEstimate"
    : null;

  if (!currentStage) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Popup
          content={
            <div>
              <p style={{ fontWeight: "bold" }}>
                {stageMap[currentStage]?.name}
              </p>
              {stageMap[currentStage]?.description}
            </div>
          }
          trigger={
            <div style={{ display: "flex", gap: 2, marginBottom: 4 }}>
              {showUserEstimate ?
                <>
                  <Icon
                    name={stageMap.userEstimate.icon}
                    style={{
                      opacity:
                        (
                          hasUserEstimate ||
                          hasProjectEstimate ||
                          hasFinalOutcome
                        ) ?
                          1
                        : 0.3,
                    }}
                  />
                  <Icon
                    name="arrow right"
                    style={{
                      opacity: hasProjectEstimate || hasFinalOutcome ? 1 : 0.3,
                    }}
                  />
                </>
              : null}
              <Icon
                name={stageMap.projectEstimate.icon}
                style={{
                  opacity: hasProjectEstimate || hasFinalOutcome ? 1 : 0.3,
                }}
              />
              <Icon
                name="arrow right"
                style={{ opacity: hasFinalOutcome ? 1 : 0.3 }}
              />
              <Icon
                name={stageMap.finalOutcome.icon}
                style={{ opacity: hasFinalOutcome ? 1 : 0.3 }}
              />
            </div>
          }
          position="top left"
          size="small"
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0px",
          }}
        >
          {hiddenImpactsCount > 0 ?
            <Popup
              mountNode={document.getElementById("semantic-modal-mount-node")}
              on="click"
              trigger={
                <button
                  type="button"
                  style={{
                    cursor: "pointer",
                    fontSize: "11px",
                    color: theme.secondaryColour,
                    background: "none",
                    border: "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {hiddenImpactsCount === allImpacts?.length ?
                    `Show (${hiddenImpactsCount})`
                  : `+ ${hiddenImpactsCount} more`}
                </button>
              }
              content={
                <div>
                  <h4>All {t("generic.impacts")}</h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "4px",
                    }}
                  >
                    {allImpacts.map((i: IdeaImpactDisplayMeasure) => (
                      <Popup
                        key={i.impactId}
                        position="right center"
                        content={generateImpactText(true, i, t)}
                        trigger={
                          <Label
                            basic
                            color={
                              i.ownerImpact?.effect === "cost" ? "red"
                              : i.ownerImpact?.effect === "saving" ?
                                "green"
                              : "grey"
                            }
                            size="tiny"
                            style={{
                              width: "100%",
                              margin: 0,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  overflow: "hidden",
                                }}
                              >
                                <Icon
                                  name={getImpactIconName(i.ownerImpact?.type)}
                                />
                                <div
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {annualImpactValue(true, i)}
                                </div>
                              </div>
                              {i.choice?.weight !== undefined ?
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Icon
                                    name="level up alternate"
                                    style={{ marginLeft: 4, marginRight: 2 }}
                                  />
                                  {i.choice?.weight || 0}/
                                  {i.ownerImpact?.maxWeight || 0}
                                </div>
                              : null}
                            </div>
                          </Label>
                        }
                      />
                    ))}
                  </div>
                </div>
              }
            />
          : null}
        </div>
      </div>
      {pinnedImpacts.length !== 0 ?
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4px",
          }}
        >
          {pinnedImpacts.map((i: IdeaImpactDisplayMeasure) => (
            <Popup
              key={i.impactId}
              position="right center"
              content={generateImpactText(true, i, t)}
              trigger={
                <Label
                  basic
                  color={
                    i.ownerImpact?.effect === "cost" ? "red"
                    : i.ownerImpact?.effect === "saving" ?
                      "green"
                    : "grey"
                  }
                  size="tiny"
                  style={{ width: "100%", margin: 0, overflow: "hidden" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        overflow: "hidden",
                      }}
                    >
                      <Icon name={getImpactIconName(i.ownerImpact?.type)} />
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "11px",
                        }}
                      >
                        {annualImpactValue(true, i)}
                      </div>
                    </div>
                    {i.choice?.weight !== undefined ?
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Icon
                          name="level up alternate"
                          style={{ marginLeft: 4, marginRight: 2 }}
                        />
                        {i.choice?.weight || 0}/{i.ownerImpact?.maxWeight || 0}
                      </div>
                    : null}
                  </div>
                </Label>
              }
            />
          ))}
        </div>
      : null}
    </div>
  );
};
